import {
  elementaryV2ContentfulMappings,
  middleSchoolV2ContentfulMappings,
  digitalWellBeingV2ContentfulMappings,
  k8V2ContentfulMappings,
} from 'utils/productMaps'
import {
  countCompleteLessons,
  countLessonsInNode,
  countTotalLessons,
  resolveCourseTreeFromLookupKeys,
} from 'utils/lmsHelpers'

const PROGRAM_CONTENTFUL_MAPPINGS = {
  elementary: elementaryV2ContentfulMappings,
  'middle-school': middleSchoolV2ContentfulMappings,
  'digital-well-being': digitalWellBeingV2ContentfulMappings,
}

export const getClassInstanceId = classItem =>
  classItem?.instance ??
  (classItem?.registration_id != null ? String(classItem.registration_id) : '')

export const getClassTitle = classItem =>
  classItem?.title ?? classItem?.class_title ?? ''

export const getClassTier = classItem =>
  classItem?.tier ??
  (classItem?.course_name?.toLowerCase()?.includes('tier2') ? 2 : 1)

export const isClassFinished = classItem =>
  classItem?.is_finished === true ||
  classItem?.is_finished === 'true' ||
  classItem?.is_finished === 1

const getDwbCourseCodeFromGrade = gradeLabel => {
  if (!gradeLabel) return null
  return `${String(gradeLabel).toLowerCase()}_dwb_tier-1_siteEducator`
}

export const getClassCourseTreeLookupKeys = ({ classItem, program }) => {
  const rawCourseName = classItem?.course_name
  const candidates = []
  const addCandidate = candidate => {
    if (!candidate || candidates.includes(candidate)) return
    candidates.push(candidate)
  }

  addCandidate(rawCourseName)

  const normalizedGrade = normalizeCourseNameForMapping(rawCourseName)
  if (!normalizedGrade) return candidates

  addCandidate(normalizedGrade)
  addCandidate(normalizedGrade === 'k' ? 'K' : normalizedGrade)
  addCandidate(`grade-${normalizedGrade}`)

  if (program === 'digital-well-being') {
    addCandidate(getDwbCourseCodeFromGrade(normalizedGrade))
  }

  return candidates
}

export const getClassTotalLessons = (classItem, courseManager, program) => {
  if (classItem?.totalLessons != null) return classItem.totalLessons

  const lookupKeys = getClassCourseTreeLookupKeys({ classItem, program })
  const courseTrees = courseManager?.courseTrees

  if (courseTrees) {
    const courseTree = resolveCourseTreeFromLookupKeys({
      courseTrees,
      lookupKeys,
    })
    if (courseTree) return countLessonsInNode(courseTree)
  }

  return countTotalLessons(classItem, courseManager, lookupKeys)
}

export const getClassCompleteLessons = classItem =>
  classItem?.completeLessons ?? countCompleteLessons(classItem)

const getTier1CourseLinkFromContentfulId = (courseLinks, classItem) => {
  const contentfulID = classItem?.contentfulID
  if (!contentfulID) return null

  return courseLinks?.find(link => contentfulID === link?.entryId) || null
}

/** Normalizes LMS course_name values (e.g. "5_dwb_tier-1_siteEducator" -> "5"). */
export const normalizeCourseNameForMapping = courseName => {
  const normalized = String(courseName || '')
    .trim()
    .toLowerCase()
  if (!normalized) return ''

  const dwbCourseCodeMatch = normalized.match(/^(k|\d{1,2})_dwb_/)
  if (dwbCourseCodeMatch?.[1]) {
    return dwbCourseCodeMatch[1]
  }

  return normalized
}

const getGradeMappingFromCourseName = ({ courseName, program }) => {
  const normalizedCourseName = normalizeCourseNameForMapping(courseName)
  if (!normalizedCourseName) return null

  const mappings = PROGRAM_CONTENTFUL_MAPPINGS[program] || []
  return (
    mappings.find(
      entry => entry?.courseName?.toLowerCase() === normalizedCourseName,
    ) || null
  )
}

const extractGradeFromCourseName = rawCourseName => {
  const normalizedCourseName = normalizeCourseNameForMapping(rawCourseName)
  if (!normalizedCourseName) return ''

  if (/^(k|\d{1,2})$/.test(normalizedCourseName)) {
    return normalizedCourseName
  }

  const gradePrefixMatch = normalizedCourseName.match(/^grade[\s-]*(k|\d{1,2})/)
  if (gradePrefixMatch?.[1]) {
    return gradePrefixMatch[1]
  }

  return ''
}

export const getClassGradeSlug = ({ classItem, courseTier1, program }) => {
  const mapping = getGradeMappingFromCourseName({
    courseName: classItem?.course_name,
    program,
  })
  if (mapping?.slug) return mapping.slug

  const slugFromCourseLink = String(courseTier1?.slug || '')
    .trim()
    .replace(/^\/+|\/+$/g, '')
  if (slugFromCourseLink) return slugFromCourseLink

  const extractedGrade = extractGradeFromCourseName(classItem?.course_name)
  if (extractedGrade) return `grade-${extractedGrade}`

  const rawCourseName = String(classItem?.course_name || '').trim()
  if (!rawCourseName) return ''

  const withoutGradePrefix = rawCourseName.replace(/^grade[\s-]*/i, '')
  return `grade-${withoutGradePrefix}`
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/^\/+|\/+$/g, '')
}

const getGradeDisplayTitleFromCourseName = ({ courseName, program }) =>
  getGradeMappingFromCourseName({ courseName, program })?.displayTitle || ''

const getCourseLinkForV2Class = ({ classItem, courseLinks, program }) => {
  const mapping = getGradeMappingFromCourseName({
    courseName: classItem?.course_name,
    program,
  })
  if (!mapping) return null

  return (
    courseLinks?.find(
      link =>
        link?.title === mapping.displayTitle ||
        (mapping.slug && link?.slug === mapping.slug) ||
        (mapping.id && link?.entryId === mapping.id),
    ) || null
  )
}

const getTier2ContentfulMapping = (classItem, program) => {
  if (classItem?.contentfulID) {
    return (
      k8V2ContentfulMappings?.find(
        course => classItem.contentfulID === course?.id,
      ) || {}
    )
  }

  const mappings =
    PROGRAM_CONTENTFUL_MAPPINGS[program] || k8V2ContentfulMappings
  return (
    mappings.find(
      entry =>
        entry?.courseName?.toLowerCase() ===
        String(classItem?.course_name || '').toLowerCase(),
    ) || {}
  )
}

export const getClassCardDisplayData = ({
  classItem,
  courseLinks,
  tier,
  program,
}) => {
  const courseTier1 =
    tier === 1
      ? getCourseLinkForV2Class({ classItem, courseLinks, program }) ||
        getTier1CourseLinkFromContentfulId(courseLinks, classItem)
      : null
  const { displayTitle: tier2DisplayTitle, defaultUnit } =
    tier === 2 ? getTier2ContentfulMapping(classItem, program) : {}

  const gradeDisplayTitle =
    tier === 2
      ? tier2DisplayTitle
      : getGradeDisplayTitleFromCourseName({
          courseName: classItem?.course_name,
          program,
        }) || courseTier1?.title

  const gradeSlug =
    tier === 1 ? getClassGradeSlug({ classItem, courseTier1, program }) : ''

  return {
    courseTier1,
    defaultUnit,
    gradeDisplayTitle,
    gradeSlug,
  }
}
